<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import DotVotingStatus from '@/components/DotVotingStatus.vue';
import DotVotingChoice from '@/components/DotVotingChoice.vue';

import { DotVotingStep } from '@/store/form.Interface';
import { Locales } from '@/store/language.interface';
import { constant } from '@/constant';
const State = constant.FormAnswer.FormState;

// PROPS
const props = defineProps<{
  step: DotVotingStep;
  languageEnabled?: Locales[];
  statistics: [];
  userVote?: [];
  state: any;
}>();

// EMITS
const emit = defineEmits<{
  'update:modelValue': [{ answerId: number; valueId: number | null }[]];
}>();

defineExpose({
  resetValue,
});

// FUNCTIONS
const mapUserVotesToSelectedDotIds = (userVote: any): Record<number, number | null> | null => {
  const votes = Array.isArray(userVote) ? userVote[0]?.votes : null;
  if (!Array.isArray(votes) || votes.length === 0) return null;

  return votes.reduce<Record<number, number | null>>((acc, vote) => {
    if (!vote || typeof vote.answer_id !== 'number') return acc;

    const voteValue = vote.value_id === undefined || vote.value_id === null ? null : Number(vote.value_id);
    acc[vote.answer_id] = Number.isFinite(voteValue) ? voteValue : null;
    return acc;
  }, {});
};

// REFS
const selectedDotIds = ref<Record<number, number | null>>({});
const isStatusSticky = ref(false);
const statusRef = ref<HTMLElement | null>(null);

const updateStatusStickyState = () => {
  if (!statusRef.value) return;

  const stickyOffset = parseFloat(getComputedStyle(statusRef.value).top || '0');
  const { top } = statusRef.value.getBoundingClientRect();
  isStatusSticky.value = top <= stickyOffset;
};

onMounted(() => {
  updateStatusStickyState();
  window.addEventListener('scroll', updateStatusStickyState, { passive: true });
  window.addEventListener('resize', updateStatusStickyState);
});

onBeforeUnmount(() => {
  window.removeEventListener('scroll', updateStatusStickyState);
  window.removeEventListener('resize', updateStatusStickyState);
});

watch(
  () => props.userVote,
  () => {
    const votesFromUser = mapUserVotesToSelectedDotIds(props.userVote);
    if (votesFromUser) {
      selectedDotIds.value = votesFromUser;
    }
  },
  { deep: true },
);

watch(
  () => props.state,
  (newState) => {
    if (newState === State.Question) {
      selectedDotIds.value = {};
    } else {
      const votesFromUser = mapUserVotesToSelectedDotIds(props.userVote);
      if (votesFromUser) {
        selectedDotIds.value = votesFromUser;
      }
    }
  },
  { immediate: true },
);

// COMPUTED
const remainingBudget = computed(() => {
  const { total_budget, dot_voting_type, dots, default_cost_per_choice } = props.step;
  if (!total_budget) return 0;

  if (dot_voting_type === constant.Form.DotVoting.Type.Fixed) {
    const usedBudget = Object.entries(selectedDotIds.value).reduce((total, [dotId, _]) => {
      const dot = dots.find((d) => d.id === Number(dotId));
      if (!dot) return total;

      const dotValue = Number(dot.cost || default_cost_per_choice || 0);
      return total + dotValue;
    }, 0);
    return total_budget - usedBudget;
  } else if (dot_voting_type === constant.Form.DotVoting.Type.Variable) {
    const usedBudget = Object.entries(selectedDotIds.value).reduce((total, [dotId, index]) => {
      const dot = dots.find((d) => d.id === Number(dotId));
      const stepSize = (dot.cost || default_cost_per_choice || 0) / (props.step.number_of_steps || 1);
      return total + index * stepSize;
    }, 0);
    return total_budget - usedBudget;
  }

  return 0;
});

const hasUserVoted = computed(() => {
  return props.userVote && props.userVote.length > 0;
});

const emitSelectedDots = () => {
  emit(
    'update:modelValue',
    Object.entries(selectedDotIds.value)
      .filter(([_dotId, value]) => (value ?? 1) > 0)
      .map(([dotId, value]) => ({
        answerId: parseInt(dotId, 10),
        valueId: value,
      })),
  );
};

const canSelectDot = (dotId: number, sliderValue?: number): boolean => {
  if (dotId in selectedDotIds.value) return true;
  const { dot_voting_type, default_cost_per_choice, dots, number_of_steps } = props.step;
  const dot = dots.find((d) => d.id === dotId);
  if (!dot) {
    console.debug('Dot not found for id:', dotId);
    return false;
  }

  if (dot_voting_type === constant.Form.DotVoting.Type.Fixed) {
    const dotValue = dot.cost || default_cost_per_choice || 0;
    return remainingBudget.value >= dotValue;
  } else if (dot_voting_type === constant.Form.DotVoting.Type.Variable) {
    const stepSize = (dot.cost || default_cost_per_choice || 0) / (number_of_steps || 1);
    return remainingBudget.value >= (sliderValue || 0) * stepSize;
  }

  return false;
};

const toggleDotSelection = (dotId: number, sliderValue?: number) => {
  if (dotId in selectedDotIds.value) {
    delete selectedDotIds.value[dotId];
  } else {
    if (!canSelectDot(dotId, sliderValue)) return;

    if (props.step.dot_voting_type === constant.Form.DotVoting.Type.Fixed) {
      selectedDotIds.value[dotId] = null;
    } else if (props.step.dot_voting_type === constant.Form.DotVoting.Type.Variable) {
      selectedDotIds.value[dotId] = sliderValue || 1;
    }
  }

  emitSelectedDots();
};

const isDotSelected = (dotId: number): boolean => {
  return dotId in selectedDotIds.value;
};

const hasUserVotedOnDot = (dotId: number): boolean => {
  if (!props.userVote || !Array.isArray(props.userVote)) return false;
  if (!props.userVote[0] || !props.userVote[0].votes || !Array.isArray(props.userVote[0].votes)) return false;

  return props.userVote[0].votes.some((vote) => vote.answer_id === dotId);
};

const getDotStatistics = (dotId: number) => {
  if (!props.statistics || typeof props.statistics !== 'object') return {};
  return props.statistics?.step?.[dotId] || {};
};

const updateVariableDotVote = (value: { [dotId: number]: number | null }) => {
  const updated = { ...selectedDotIds.value };
  Object.entries(value).forEach(([dotId, index]) => {
    const numericIndex = Number(index) || 0;
    updated[Number(dotId)] = numericIndex;
  });
  selectedDotIds.value = updated;
  emitSelectedDots();
};

function resetValue() {
  console.log('Resetting dot voting selection');
  selectedDotIds.value = {};
}
</script>
<template>
  <div class="base-question-dot-voting">
    <div
      ref="statusRef"
      class="base-question-dot-voting__status"
      :class="{ 'base-question-dot-voting__status--sticky': isStatusSticky }"
    >
      <DotVotingStatus
        :total-budget="props.step.total_budget"
        :remaining-budget="remainingBudget"
        :dot-voting-measurement-type="props.step.dot_voting_measurement_type"
        :language-enabled="props.languageEnabled"
        :is-sticky="isStatusSticky"
      />
    </div>
    <div v-for="dot in props.step.dots" :key="dot.id" class="base-question-dot-voting__dots">
      <DotVotingChoice
        :dot="dot"
        :dot-voting-type="props.step.dot_voting_type"
        :language-enabled="props.languageEnabled"
        :dot-voting-measurement-type="props.step.dot_voting_measurement_type"
        :default-cost-per-choice="props.step.default_cost_per_choice"
        :number-of-steps="props.step.number_of_steps"
        :remaining-budget="remainingBudget"
        :is-selected="isDotSelected(dot.id)"
        :selected-value="selectedDotIds[dot.id]"
        :is-disabled="!canSelectDot(dot.id)"
        :has-user-voted="hasUserVoted"
        :has-user-voted-on-choice="hasUserVotedOnDot(dot.id)"
        :state="props.state"
        :statistics="getDotStatistics(dot.id)"
        @select-vote="(dotId, selectedValue) => toggleDotSelection(dotId, selectedValue)"
        @update:slider-value="updateVariableDotVote"
      />
    </div>
  </div>
</template>
<style lang="scss">
.base-question-dot-voting {
  display: flex;
  flex-direction: column;
  gap: $space-md;
  margin-bottom: $space-sm;
  position: relative;
  z-index: 0; // create stacking context so header z-index still wins

  &__status {
    position: sticky;
    top: $header-height;
    z-index: 2; // above card content, below header stacking context
    background: $color-neutral-white;
    transition:
      padding 0.3s ease,
      box-shadow 0.3s ease;

    &--sticky {
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
  }

  &__dots {
    display: flex;
    flex-direction: column;
    gap: $space-md;
  }
}

@media screen and (max-width: $breakpoint-sm) {
  .base-question-dot-voting {
    &__status {
      top: 56px; // 56px = mobile header height
    }
  }
}
</style>
